import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../config/env.js';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user and token from localStorage on mount
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('mediscan_user');
      const storedToken = localStorage.getItem('mediscan_token');
      if (storedUser && storedToken) {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
      } else {
        // Clear partial state
        localStorage.removeItem('mediscan_user');
        localStorage.removeItem('mediscan_token');
      }
    } catch (error) {
      console.error('Failed to load user from localStorage:', error);
      localStorage.removeItem('mediscan_user');
      localStorage.removeItem('mediscan_token');
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(getApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Login failed');
      }

      const userData = {
        id: data.doctor.id,
        tenantId: data.doctor.tenant_id,
        name: data.doctor.name,
        email: data.doctor.email,
        specialty: data.doctor.specialty,
        licenseNumber: data.doctor.license_number,
        credentials: data.doctor.credentials,
      };

      setUser(userData);
      setToken(data.token);
      localStorage.setItem('mediscan_user', JSON.stringify(userData));
      localStorage.setItem('mediscan_token', data.token);

      // Legacy doctorInfo for backward compatibility
      const legacyDoctorInfo = {
        name: userData.name,
        specialty: userData.specialty,
        place: 'Medical Office',
        consultationType: 'Consultation générale',
      };
      localStorage.setItem('doctorInfo', JSON.stringify(legacyDoctorInfo));

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Demo-mode password gate — exchanges a shared password for a JWT bound to
  // the fixed demo-doctor/demo-tenant identity. Used only when VITE_DEMO_MODE=true.
  const enterGate = async (password) => {
    try {
      const response = await fetch(getApiUrl('/api/auth/gate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Incorrect password');
      }

      const userData = {
        id: data.doctor.id,
        tenantId: data.doctor.tenant_id,
        name: data.doctor.name,
        email: data.doctor.email || '',
        specialty: data.doctor.specialty,
        licenseNumber: '',
        credentials: '',
      };

      setUser(userData);
      setToken(data.token);
      localStorage.setItem('mediscan_user', JSON.stringify(userData));
      localStorage.setItem('mediscan_token', data.token);

      // doctorInfo seeds downstream components that read from localStorage
      const legacyDoctorInfo = {
        name: userData.name,
        specialty: userData.specialty,
        place: 'Demo Office',
        consultationType: 'Consultation générale',
      };
      localStorage.setItem('doctorInfo', JSON.stringify(legacyDoctorInfo));

      return { success: true, user: userData };
    } catch (error) {
      console.error('Gate error:', error);
      return { success: false, error: error.message };
    }
  };

  const register = async (registrationData) => {
    try {
      const response = await fetch(getApiUrl('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Registration failed');
      }

      const userData = {
        id: data.doctor.id,
        tenantId: data.doctor.tenant_id,
        name: data.doctor.name,
        email: data.doctor.email,
        specialty: data.doctor.specialty,
        licenseNumber: data.doctor.license_number,
        credentials: data.doctor.credentials,
      };

      setUser(userData);
      setToken(data.token);
      localStorage.setItem('mediscan_user', JSON.stringify(userData));
      localStorage.setItem('mediscan_token', data.token);

      const legacyDoctorInfo = {
        name: userData.name,
        specialty: userData.specialty,
        place: 'Medical Office',
        consultationType: 'Consultation générale',
      };
      localStorage.setItem('doctorInfo', JSON.stringify(legacyDoctorInfo));

      return { success: true, user: userData };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('mediscan_user');
    localStorage.removeItem('mediscan_token');
    localStorage.removeItem('doctorInfo');
  }, []);

  const updateUserProfile = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('mediscan_user', JSON.stringify(updatedUser));

    if (updates.name || updates.specialty) {
      const doctorInfo = JSON.parse(localStorage.getItem('doctorInfo') || '{}');
      const updatedDoctorInfo = {
        ...doctorInfo,
        name: updates.name || doctorInfo.name,
        specialty: updates.specialty || doctorInfo.specialty,
      };
      localStorage.setItem('doctorInfo', JSON.stringify(updatedDoctorInfo));
    }
  };

  // Authenticated fetch wrapper — attaches Bearer token, auto-logout on 401
  const authFetch = useCallback(async (url, options = {}) => {
    const currentToken = localStorage.getItem('mediscan_token');
    if (!currentToken) {
      logout();
      throw new Error('Not authenticated');
    }

    const headers = {
      ...options.headers,
    };

    // Don't set Content-Type for FormData — browser sets it with boundary
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }
    headers['Authorization'] = `Bearer ${currentToken}`;

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      logout();
      throw new Error('Session expired — please log in again');
    }

    return response;
  }, [logout]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    register,
    enterGate,
    logout,
    updateUserProfile,
    authFetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
